import { CloseButton, Dialog, Flex, Heading, Separator, Text } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';
import { IoArrowBack } from 'react-icons/io5';

import {
  ChangeEvent,
  Menu,
  MenuContent,
  MenuGroup,
  MenuItem,
  MenuRef,
} from '@components/common/Menu';
import { NAVIGATION_TOP_BAR_HEIGHT_PX } from 'config/layout/navigationPanel';
import { toPxString } from 'helpers/styles';
import { useSettingsNavigationEntities } from 'hooks/useNavigationEntities';
import useOrgSettingOptions from 'hooks/useOrgSettings';
import RawArrowIcon from 'vectors/Arrowhead';

interface SettingsListProps {
  showSelected?: boolean;
  showRightArrow?: boolean;
  onClickNavigationItem?: () => void;
}

const SettingsList = observer(
  ({ showSelected = true, showRightArrow = false, onClickNavigationItem }: SettingsListProps) => {
    const menuRef = useRef<MenuRef>(null);
    const { sections } = useOrgSettingOptions();
    const { byId: settingsById, selectedNavigationEntity } = useSettingsNavigationEntities();
    const router = useRouter();

    const onSelect = useCallback(
      (e: ChangeEvent) => {
        onClickNavigationItem?.();
        const selected = settingsById[e.value];
        if (selected == null || selected.absolutePath == null) {
          return;
        }
        router.push(selected.absolutePath);
      },
      [settingsById, router, onClickNavigationItem],
    );

    return (
      <Menu
        showSelected
        value={selectedNavigationEntity?.id}
        ref={menuRef}
        onChange={onSelect}
        focus
      >
        {sections.map((section) => (
          <MenuGroup key={section.id} title={section.name}>
            {section.options.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                pr="2px"
                justifyContent="space-between"
                alignItems="center"
                textOverflow="ellipsis"
                fontSize="md"
                showSelected={showSelected}
              >
                <MenuContent
                  value={
                    <Flex
                      alignItems="center"
                      gap={2}
                      py={1}
                      justifyContent="space-between"
                      w="full"
                    >
                      <Text textAlign="left">{settingsById[option.id]?.name}</Text>
                      {showRightArrow && <RawArrowIcon boxSize="10px" direction="right" />}
                    </Flex>
                  }
                />
              </MenuItem>
            ))}
          </MenuGroup>
        ))}
      </Menu>
    );
  },
);

interface SettingsContentProps {
  showHeader?: boolean;
}

const SettingsContent = observer(({ showHeader = true }: SettingsContentProps) => {
  const { selectedNavigationEntity } = useSettingsNavigationEntities();
  return (
    <Flex flexDirection="column" gap={2} w="full" h="full">
      {showHeader && (
        <>
          <Heading>{selectedNavigationEntity?.name}</Heading>
          <Separator borderColor="border.light" />
        </>
      )}
      <Text>Content</Text>
    </Flex>
  );
});

const WidescreenSettingsContent = observer(() => {
  return (
    <Flex flexDirection="column" gap={2} w="full" h="full" p={2}>
      <SettingsTitle showBack={false} />
      <Flex flexDirection="row" gap={2} h="full">
        <Flex width={MENU_WIDTH} flexDirection="column" px={1} minH="lg" gap={1}>
          <SettingsList />
        </Flex>
        <Separator orientation="vertical" borderColor="border.light" />
        <Flex mx="auto" px="md" w="2xl" flexDirection="column" gap={2}>
          <SettingsContent />
        </Flex>
      </Flex>
    </Flex>
  );
});

const MobileSettingsContent = observer(() => {
  const { selectedNavigationEntity } = useSettingsNavigationEntities();
  const [showBack, setShowBack] = useState(selectedNavigationEntity != null);

  const onBack = useCallback(() => {
    setShowBack(false);
  }, []);

  const onClickNavigationItem = useCallback(() => {
    setShowBack(true);
  }, []);

  return (
    <Flex flexDirection="column" gap={2} w="full" h="full" p={2}>
      <SettingsTitle showBack={showBack} onBack={onBack} />
      {!showBack ? (
        <SettingsList
          showSelected={false}
          showRightArrow
          onClickNavigationItem={onClickNavigationItem}
        />
      ) : (
        <Flex px={2}>
          <SettingsContent showHeader={false} />
        </Flex>
      )}
    </Flex>
  );
});

const SettingsTitleContent = observer(
  ({ showBack, onBack }: { showBack?: boolean; onBack?: () => void }) => {
    const { selectedNavigationEntity } = useSettingsNavigationEntities();

    if (!showBack) {
      return <Dialog.Title>Settings</Dialog.Title>;
    }

    return (
      <Flex flexDirection="column" gap={4} w="full">
        {selectedNavigationEntity == null ? (
          <Dialog.Title>Settings</Dialog.Title>
        ) : (
          <Flex
            flexDirection="row"
            gap={2}
            w="full"
            h="full"
            alignItems="center"
            onClick={onBack}
            cursor="pointer"
          >
            <IoArrowBack />
            <Dialog.Title>Settings</Dialog.Title>
          </Flex>
        )}
      </Flex>
    );
  },
);

const SettingsTitle = observer(
  ({ showBack, onBack }: { showBack?: boolean; onBack?: () => void }) => {
    const { closeSettings } = useSettingsNavigationEntities();

    return (
      <Flex flexDirection="row" justifyContent="space-between" w="full">
        <Flex justifyContent="center" alignSelf="center">
          <SettingsTitleContent showBack={showBack} onBack={onBack} />
        </Flex>
        <CloseButton
          display="flex"
          size="sm"
          _hover={{ bgColor: 'button.secondary.hover' }}
          bgColor="button.secondary.hover"
          onClick={closeSettings}
        />
      </Flex>
    );
  },
);

const MENU_WIDTH = '250px';

const OrgSettingsModal = () => {
  const { isOpen, closeSettings } = useSettingsNavigationEntities();

  return (
    <Dialog.Root size="full" lazyMount open={isOpen} onOpenChange={closeSettings}>
      <Dialog.Backdrop />
      <Dialog.Positioner mt={toPxString(NAVIGATION_TOP_BAR_HEIGHT_PX)} borderRadius="md">
        <Dialog.Content bgColor="surface.background">
          <Dialog.Body p={2}>
            <Flex flexDirection="row" gap={2} h="full" hideBelow="lg">
              <WidescreenSettingsContent />
            </Flex>
            <Flex flexDirection="column" gap={4} hideFrom="lg" w="full" h="full" p={2}>
              <MobileSettingsContent />
            </Flex>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

export default observer(OrgSettingsModal);
